/**
 * Generative Art renderer dispatcher.
 * Unified entry point: renderGenerator(ctx, W, H, config)
 *
 * Looks up the generator + palette from config, applies color adjustments,
 * seeds the appropriate RNG/noise engine, and dispatches to the correct renderer.
 */

import { createNoiseEngine } from '../noise';
import { createRandom } from '../seededRandom';
import { PALETTE_MAP, GENERATOR_MAP, preparePalette, getDefaultParams } from '../../constants/genArt';

// ── Organic renderers ──
import {
  renderCirclePack, renderStipple, renderErosion, renderSmoke,
  renderParticles, renderTopography, renderDomainWarp, renderRidged,
  renderSilkThreads, renderFluid,
} from './organic';

// ── Geo Simple renderers ──
import {
  renderHalftonePanel, renderBlockMaze, renderTruchetCurves,
  renderGeoComposition, renderConcentricFrames, renderSunburst,
} from './geoSimple';

// ── Poster (Parametric) renderers ──
import { renderBlockMosaic, renderCheckerWarp, renderLineField } from './geoParametric';

// ── Strata renderer ──
import { renderStrata } from './strata';

// ── Meridian renderer ──
import { renderMeridian } from './meridian';

// ── Normie Portrait renderer (sync fallback for thumbnails) ──
import { renderNormiePortraitSync } from './normiePortrait';

// ═══════════════════════════════════════════════════════════
//  RENDERER MAP
// ═══════════════════════════════════════════════════════════

const ORGANIC_RENDERERS = {
  circlePack:   renderCirclePack,
  stipple:      renderStipple,
  erosion:      renderErosion,
  smoke:        renderSmoke,
  particles:    renderParticles,
  topography:   renderTopography,
  domainWarp:   renderDomainWarp,
  ridged:       renderRidged,
  silkThreads:  renderSilkThreads,
  fluid:        renderFluid,
};

const GEO_RENDERERS = {
  halftonePanel:    renderHalftonePanel,
  blockMaze:        renderBlockMaze,
  truchetCurves:    renderTruchetCurves,
  geoComposition:   renderGeoComposition,
  concentricFrames: renderConcentricFrames,
  sunburst:         renderSunburst,
};

const POSTER_RENDERERS = {
  blockMosaic: renderBlockMosaic,
  checkerWarp: renderCheckerWarp,
  lineField:   renderLineField,
};

const STRATA_RENDERERS = {
  strata: renderStrata,
  meridian: renderMeridian,
};

const NORMIES_RENDERERS = {
  normiePortrait: renderNormiePortraitSync,
};

// ═══════════════════════════════════════════════════════════
//  MAIN DISPATCHER
// ═══════════════════════════════════════════════════════════

/**
 * Render a generative art background to a canvas context.
 *
 * @param {CanvasRenderingContext2D} ctx — target canvas context
 * @param {number} W — canvas width
 * @param {number} H — canvas height
 * @param {Object} config — generative art configuration
 * @param {string} config.type — generator ID (e.g. 'ridged', 'halftonePanel')
 * @param {string} config.paletteId — palette ID (e.g. 'organic-ocean')
 * @param {number} [config.seed=42] — random seed
 * @param {number} [config.hueShift=0] — hue shift in degrees (0–360)
 * @param {number} [config.saturation=100] — saturation percentage (0–200)
 * @param {number} [config.brightness=100] — brightness percentage (50–150)
 * @param {Object} [config.params] — per-generator parameters
 */
export function renderGenerator(ctx, W, H, config) {
  const {
    type,
    paletteId,
    seed = 42,
    hueShift = 0,
    saturation = 100,
    brightness = 100,
    params = {},
  } = config;

  // Look up palette
  const rawPalette = PALETTE_MAP[paletteId];
  if (!rawPalette) {
    console.warn(`[genArt] Unknown palette: ${paletteId}`);
    ctx.fillStyle = '#1a1a1a'; ctx.fillRect(0, 0, W, H);
    return;
  }

  // Look up generator
  const gen = GENERATOR_MAP[type];
  if (!gen) {
    console.warn(`[genArt] Unknown generator: ${type}`);
    ctx.fillStyle = '#1a1a1a'; ctx.fillRect(0, 0, W, H);
    return;
  }

  // Apply palette adjustments
  const satMul = saturation / 100;
  const briMul = brightness / 100;
  const palette = preparePalette(rawPalette, hueShift, satMul, briMul);

  // Merge default params with provided params
  const defaults = getDefaultParams(type);
  const mergedParams = { ...defaults, ...params };

  // Dispatch based on family
  const { family } = gen;

  if (family === 'organic' || family === 'flow') {
    const engine = createNoiseEngine(seed);
    const renderer = ORGANIC_RENDERERS[type];
    if (renderer) renderer(ctx, W, H, palette, mergedParams, engine);
    else console.warn(`[genArt] No organic renderer for: ${type}`);
  }
  else if (family === 'geo') {
    const rand = createRandom(seed);
    const renderer = GEO_RENDERERS[type];
    if (renderer) renderer(ctx, W, H, palette, mergedParams, rand);
    else console.warn(`[genArt] No geo renderer for: ${type}`);
  }
  else if (family === 'poster') {
    const rand = createRandom(seed);
    const renderer = POSTER_RENDERERS[type];
    if (renderer) renderer(ctx, W, H, palette, mergedParams, rand);
    else console.warn(`[genArt] No poster renderer for: ${type}`);
  }
  else if (family === 'strata') {
    const rand = createRandom(seed);
    const renderer = STRATA_RENDERERS[type];
    if (renderer) renderer(ctx, W, H, palette, mergedParams, rand);
    else console.warn(`[genArt] No strata renderer for: ${type}`);
  }
  else if (family === 'normies') {
    const renderer = NORMIES_RENDERERS[type];
    if (renderer) renderer(ctx, W, H, palette, mergedParams);
    else console.warn(`[genArt] No normies renderer for: ${type}`);
  }
  else {
    console.warn(`[genArt] Unknown family: ${family}`);
    ctx.fillStyle = '#1a1a1a'; ctx.fillRect(0, 0, W, H);
  }
}

/**
 * Render a thumbnail (smaller version for UI buttons).
 * Renders at the specified resolution. Use higher res for quality.
 */
export function renderThumbnail(type, paletteId, seed, thumbW = 192, thumbH = 240) {
  const canvas = document.createElement('canvas');
  canvas.width = thumbW;
  canvas.height = thumbH;
  const ctx = canvas.getContext('2d');

  renderGenerator(ctx, thumbW, thumbH, {
    type,
    paletteId,
    seed,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
  });

  return canvas.toDataURL('image/jpeg', 0.85);
}

// Re-export for convenience
export { GENERATORS, FAMILIES, PALETTES, PALETTE_MAP, GENERATOR_MAP } from '../../constants/genArt';

// Re-export async renderers for GenerativeBackground
export { renderStrataAsync, isStrataWorkerAvailable, renderMeridianAsync, isMeridianWorkerAvailable } from './strataWorkerManager';
export { renderNormiePortraitAsync } from './normiePortrait';
// Re-export palette utilities for thumbnail rendering
export { preparePalette } from '../../constants/genArt';
